import { BaseObjectValue } from "@/types/common";
import type ArmsRum from "@arms/rum-browser";
import { sendGTMEvent } from "@next/third-parties/google";
import { BUILD_VERSION, IS_SERVER } from "./const";
import { DeployEnv, VIDMUSE_DEPLOY_ENV } from "./env";
import { VidMuseEvent } from "./events";

type LoggerFunctionName = string;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LoggerFunctionParams = any[];

export interface LoggerEventParams {
  type?: string;
  value?: number;
  [key: string]: BaseObjectValue;
}

export class Logger {
  private isInitialized: boolean = false;
  private isFlushing: boolean = false;

  ArmsRumClass: typeof ArmsRum | null = null;
  armsIns?: ReturnType<typeof ArmsRum.init>;
  consumers = new Map<
    LoggerFunctionName,
    (...args: LoggerFunctionParams) => void
  >();
  queue: Array<[LoggerFunctionName, LoggerFunctionParams]> = [];

  constructor() {
    this.init();
    this.consumers.set("setUser", this.setUser.bind(this));
    this.consumers.set("event", this.event.bind(this));
  }

  init() {
    if (!IS_SERVER && !this.isInitialized && window?.RumSDK) {
      this.ArmsRumClass = window.RumSDK.default;
      this.armsIns = window.RumSDK.default.init({
        pid: "fa6f9pxqjp@a58870121cb5a19",
        endpoint: "https://fa6f9pxqjp-default-cn.rum.aliyuncs.com",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        env: VIDMUSE_DEPLOY_ENV,
        version: BUILD_VERSION,
        spaMode: "history",
        tracing: true,
      });

      console.log("VidMuse Logger initialized.");

      this.isInitialized = true;
      // 初始化完成后刷新队列，此时 isFlushing 为 false，可以安全调用
      this.flushQueue();
    }
  }

  flushQueue() {
    if (IS_SERVER) {
      console.warn("Logger method is not available on server");
      return;
    }

    // 防止递归调用导致死循环
    if (this.isFlushing) {
      return;
    }

    if (!this.isInitialized) {
      this.init();
      return;
    }

    this.isFlushing = true;

    try {
      while (this.queue.length > 0) {
        const [name, params] = this.queue.shift()!;
        const consumer = this.consumers.get(name);

        if (consumer) {
          consumer(...params);
        }
      }
    } finally {
      this.isFlushing = false;
    }
  }

  setUser(userId: string) {
    if (IS_SERVER) {
      console.warn("Logger method is not available on server");
      return;
    }

    if (!this.isInitialized) {
      this.queue.push(["setUser", [userId]]);
      return;
    }

    if (!this.isFlushing) {
      this.flushQueue();
    }

    this.ArmsRumClass?.setConfig("user", {
      name: userId,
    });

    sendGTMEvent({
      event: "set_user",
      user_id: userId,
    });
  }

  event(event: VidMuseEvent, params?: LoggerEventParams) {
    if (IS_SERVER) {
      console.warn("Logger method is not available on server");
      return;
    }

    if (!this.isInitialized) {
      this.queue.push(["event", [event, params]]);
      return;
    }

    if (!this.isFlushing) {
      this.flushQueue();
    }

    try {
      if (event.startsWith("custom_")) {
        sendGTMEvent({
          event: "ga4_custom_event",
          event_name: event,
          event_params: params,
        });
      } else {
        sendGTMEvent({
          event,
          ...params,
        });
      }

      // ========= ARMS EVENT CONFIG START =========
      const { type = "unknown", value = 0, ...properties } = params || {};
      // const normalizedProperties: Record<string, BaseObjectValue> = {};

      // Object.entries(properties).forEach(([key, value]) => {
      //   if (typeof value === "boolean") {
      //     normalizedProperties[key] = value ? "yes" : "no";
      //     return;
      //   }

      //   normalizedProperties[key] = value;
      // });

      this.armsIns?.sendCustom({
        name: event,
        type,
        value,
        properties,
      });

      // ========= ARMS EVENT CONFIG END   =========

      if (VIDMUSE_DEPLOY_ENV !== DeployEnv.Prod) {
        console.log("event: ", event, params);
      }
    } catch (error) {
      console.error("event error", error);
    }
  }
}

export const logger = new Logger();
